// Money is integer cents everywhere. These are the only functions that convert
// between cents and text — nothing else in the app should do arithmetic on a
// dollar-valued float, because 0.1 + 0.2 is not 0.3.

/**
 * The family's timezone. Every date a user picks or reads is in this zone,
 * regardless of where their device thinks it is.
 *
 * Pinning it removes a whole class of confusion: a parent scheduling "Aug 15,
 * 8am" from a laptop still set to Pacific means 8am Eastern, and a kid opening
 * the app on holiday sees the same schedule their parent set at home. The IANA
 * name (not a fixed -05:00) is what makes DST correct — the runtime applies the
 * real rules, including the transition dates, so we never do offset arithmetic
 * ourselves.
 */
export const FAMILY_TZ = "America/New_York";

/** Formats cents as a currency string, e.g. 1750 -> "$17.50", -750 -> "-$7.50". */
export function formatCents(cents: number): string {
  // A missing or non-numeric amount renders as $0.00 rather than "$NaN.NaN".
  // This happens when a cached bundle meets a response shape it predates, and
  // a money figure is the last thing that should ever show a kid garbage.
  if (typeof cents !== "number" || !Number.isFinite(cents)) {
    cents = 0;
  }
  const negative = cents < 0;
  const abs = Math.abs(cents);
  // Integer division keeps this exact; no float ever holds the amount.
  const dollars = Math.floor(abs / 100);
  const remainder = abs % 100;
  const text = `$${dollars.toLocaleString("en-US")}.${String(remainder).padStart(2, "0")}`;
  return negative ? `-${text}` : text;
}

/** Formats cents without the sign, for when the sign is shown separately. */
export function formatCentsAbs(cents: number): string {
  return formatCents(Math.abs(cents));
}

/**
 * Parses user-typed dollars into integer cents.
 *
 * Returns null for anything unparseable so the caller can show a validation
 * message rather than silently recording a wrong amount. The fractional part is
 * read as digits rather than multiplied as a float, so "0.29" is exactly 29.
 */
export function parseDollarsToCents(input: string): number | null {
  const trimmed = input.trim().replace(/[$,\s]/g, "");
  if (trimmed === "") return null;

  const match = /^(-?)(\d*)(?:\.(\d{0,2}))?$/.exec(trimmed);
  if (!match) return null;

  const [, sign, whole = "", frac = ""] = match;
  if (whole === "" && frac === "") return null;

  const dollars = whole === "" ? 0 : Number.parseInt(whole, 10);
  const cents = frac === "" ? 0 : Number.parseInt(frac.padEnd(2, "0"), 10);
  if (!Number.isSafeInteger(dollars) || !Number.isFinite(cents)) return null;

  const total = dollars * 100 + cents;
  return sign === "-" ? -total : total;
}

/** A short relative or absolute date for a transaction row. */
export function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  // Rendered in the family's timezone, not the device's, so a date reads the
  // same on every screen in the household.
  const sameYear =
    localPartsFromIso(iso).date.slice(0, 4) ===
    localPartsFromIso(new Date().toISOString()).date.slice(0, 4);

  return date.toLocaleDateString("en-US", {
    timeZone: FAMILY_TZ,
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
}

/**
 * The zone's short name at a given instant — "EDT" in summer, "EST" in winter.
 *
 * Read from Intl rather than hard-coded, so the label cannot disagree with the
 * offset actually applied.
 */
export function tzLabel(iso?: string): string {
  const instant = iso ? new Date(iso) : new Date();
  const at = Number.isNaN(instant.getTime()) ? new Date() : instant;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: FAMILY_TZ,
    timeZoneName: "short",
  }).formatToParts(at);
  return parts.find((p) => p.type === "timeZoneName")?.value ?? "ET";
}

/** A date with its time, e.g. "Aug 15, 8:00 AM", in the family's timezone. */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("en-US", {
    timeZone: FAMILY_TZ,
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}


/**
 * The UTC offset of FAMILY_TZ at a given instant, in minutes.
 *
 * Derived by asking Intl to format the instant in that zone and comparing the
 * result to the same wall-clock read as UTC. This is DST-correct by
 * construction: the offset is looked up at the instant in question rather than
 * assumed, so it is -240 in July and -300 in January without a table.
 */
function tzOffsetMinutes(instant: Date): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: FAMILY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(instant);

  const get = (type: string) =>
    Number(parts.find((p) => p.type === type)?.value ?? "0");

  // Intl renders midnight as hour 24 in some engines; normalise it.
  const hour = get("hour") % 24;

  const asUTC = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    hour,
    get("minute"),
    get("second"),
  );
  return (asUTC - instant.getTime()) / 60000;
}

/**
 * Converts a wall-clock date and time in FAMILY_TZ into a UTC ISO timestamp.
 *
 * The offset is resolved in two passes. A first guess uses the offset at the
 * naive instant, which can be wrong for a time near a DST boundary; re-reading
 * the offset at that candidate instant and re-applying it settles the answer.
 * Without the second pass, times in the hours around a transition land an hour
 * off.
 *
 * Nonexistent local times (the 2am hour that DST skips in spring) resolve
 * forward to the same instant as 3am rather than failing, which is the
 * conventional and least surprising behaviour.
 */
export function isoFromLocalParts(
  dateValue: string,
  timeValue: string,
): string | undefined {
  if (!dateValue) return undefined;

  const [y, m, d] = dateValue.split("-").map(Number);
  if (!y || !m || !d) return undefined;

  const [hh, mm] = (timeValue || "00:00").split(":").map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return undefined;

  const naive = Date.UTC(y, m - 1, d, hh, mm, 0);
  const firstOffset = tzOffsetMinutes(new Date(naive));
  const firstGuess = new Date(naive - firstOffset * 60000);
  const secondOffset = tzOffsetMinutes(firstGuess);
  let settled = new Date(naive - secondOffset * 60000);
  if (Number.isNaN(settled.getTime())) return undefined;

  // A local time inside the spring-forward gap does not exist. Both passes then
  // converge on the pre-transition offset, which would silently resolve 2:30am
  // *backward* to 1:30am — an hour earlier than asked for. Detect it by reading
  // the instant back: if it does not land on the requested wall clock, shift by
  // the size of the gap so the time resolves forward instead.
  const readBack = localPartsFromIso(settled.toISOString());
  const wanted = `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
  if (readBack.time !== wanted) {
    // Shift forward by the size of the gap (offsets are negative west of UTC,
    // so the later offset is the larger number: -240 vs -300 is a 60-minute
    // jump). 2:30am then becomes the same instant as 3:30am EDT.
    const gapMinutes = Math.abs(secondOffset - firstOffset) || 60;
    settled = new Date(settled.getTime() + gapMinutes * 60000);
  }
  return settled.toISOString();
}

/**
 * An ISO timestamp for a date-only input, at noon in the family's timezone.
 *
 * Noon rather than midnight: it is the furthest point from either DST boundary,
 * so a date-only value can never slip to the previous or next day.
 */
export function isoFromDateInput(value: string): string | undefined {
  return isoFromLocalParts(value, "12:00");
}

/** Splits an ISO timestamp into date and time inputs in the family's zone. */
export function localPartsFromIso(iso: string): { date: string; time: string } {
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return { date: "", time: "" };

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: FAMILY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(instant);

  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  const hour = String(Number(get("hour")) % 24).padStart(2, "0");

  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${hour}:${get("minute")}`,
  };
}

/** The <input type="date"> value for an ISO timestamp, in the family's zone. */
export function dateInputFromIso(iso: string): string {
  return localPartsFromIso(iso).date;
}
